/**
 * User Service
 * 
 * This service layer provides an abstraction over user-related API calls.
 * It handles all user CRUD operations, filtering, and status management.
 * 
 * @module UserService
 */

import { getStaff, createStaff, updateStaff } from '../utils/apiClient';

/**
 * Fetches users/staff based on provided API parameters
 * 
 * @param {Object} apiParams - Already converted API parameters
 * @param {boolean} [apiParams.include_inactive] - Whether to include inactive users
 * @param {string} [apiParams.role] - Role filter (future use)
 * @param {string} [apiParams.facility_id] - Facility filter (future use)
 * @returns {Promise<Array>} Array of user objects
 * 
 * @example
 * const users = await UserService.getAll({ include_inactive: true });
 */
export const getAll = async (apiParams = {}) => {
  try {
    const response = await getStaff(apiParams);
    
    if (response.statusCode === 'SUCCESS') {
      return response.response || [];
    }
    
    throw new Error(response.message || 'Failed to fetch users');
  } catch (error) {
    console.error('Error in UserService.getAll:', error);
    throw new Error('Failed to fetch users. Please try again.');
  }
};

/**
 * Creates a new user/staff member
 * 
 * @param {Object} userData - User data to create
 * @param {string} userData.name - Full name of the user
 * @param {string} userData.email - Email address
 * @param {string} userData.phoneNumber - Phone number
 * @param {string} userData.role - User role
 * @param {Array} userData.facilityIds - Array of facility IDs
 * @param {string} userData.customerId - Customer ID
 * @returns {Promise<Object>} Created user response
 * 
 * @example
 * const result = await UserService.create(userData);
 */
export const create = async (userData) => {
  try {
    const response = await createStaff(userData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'User created successfully',
        data: response.data
      };
    }
    
    // Handle specific error cases
    if (response.statusCode === 'CONFLICT' && response.response?.suggestion === 'use_different_email') {
      return {
        success: false,
        message: `Email already exists: ${response.error || 'A user with this email already exists in your organization'}`,
        errorType: 'CONFLICT'
      };
    }
    
    return {
      success: false,
      message: response.error || response.message || 'Failed to create user'
    };
  } catch (error) {
    console.error('Error in UserService.create:', error);
    throw new Error('An unexpected error occurred while creating user');
  }
};

/**
 * Updates an existing user/staff member
 * 
 * @param {string} userId - ID of the user to update
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Update response
 * 
 * @example
 * const result = await UserService.update('user-id', updatedData);
 */
export const update = async (userId, userData) => {
  try {
    const response = await updateStaff(userId, userData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'User updated successfully',
        data: response.data
      };
    }
    
    // Handle specific error cases
    if (response.statusCode === 'CONFLICT' && response.response?.suggestion === 'use_different_email') {
      return {
        success: false,
        message: `Email already exists: ${response.error || 'A user with this email already exists in your organization'}`,
        errorType: 'CONFLICT'
      };
    }
    
    return {
      success: false,
      message: response.error || response.message || 'Failed to update user'
    };
  } catch (error) {
    console.error('Error in UserService.update:', error);
    throw new Error('An unexpected error occurred while updating user');
  }
};

/**
 * Toggles user status (activate/deactivate)
 * 
 * @param {string} userId - ID of the user
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Status toggle response
 * 
 * @example
 * const result = await UserService.toggleStatus('user-id', false);
 */
export const toggleStatus = async (userId, isActive) => {
  try {
    const response = await updateStaff(userId, { isActive });
    
    if (response.statusCode === 'SUCCESS') {
      const actionText = isActive ? 'activated' : 'deactivated';
      return {
        success: true,
        message: `User ${actionText} successfully`
      };
    }
    
    return {
      success: false,
      message: `Failed to ${isActive ? 'activate' : 'deactivate'} user`
    };
  } catch (error) {
    console.error('Error in UserService.toggleStatus:', error);
    const actionText = isActive ? 'activate' : 'deactivate';
    throw new Error(`Failed to ${actionText} user. Please try again.`);
  }
};



/**
 * Validates user data before submission
 * 
 * @param {Object} userData - User data to validate
 * @returns {Object} Validation result
 * 
 * @example
 * const validation = UserService.validateData(userData);
 * if (!validation.isValid) console.log(validation.errors);
 */
export const validateData = (userData) => {
  const errors = [];
  
  if (!userData.name?.trim()) {
    errors.push('Name is required');
  }
  
  if (!userData.email?.trim()) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
    errors.push('Valid email is required');
  }
  
  if (!userData.phoneNumber?.trim()) {
    errors.push('Phone number is required');
  }
  
  if (!userData.role?.trim()) {
    errors.push('Role is required');
  }
  
  if (userData.role !== 'admin' && (!userData.facilityIds || userData.facilityIds.length === 0)) {
    errors.push('At least one facility is required for non-admin users');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formats user data for API submission
 * 
 * @param {Object} userData - Raw user data from form
 * @param {string} customerId - Customer ID (only added for new users)
 * @param {boolean} isEdit - Whether this is an edit operation (partial update)
 * @returns {Object} Formatted user data
 * 
 * @example
 * const formatted = UserService.formatForSubmission(formData, customerId, false); // For new user
 * const formatted = UserService.formatForSubmission(changeData, null, true); // For edit
 */
export const formatForSubmission = (userData, customerId, isEdit = false) => {
  const formatted = { ...userData };
  
  // Only process role if it exists in the data (avoid [null] issue)
  if ('role' in userData && userData.role !== undefined && userData.role !== null) {
    formatted.role = Array.isArray(userData.role) ? userData.role : [userData.role];
  } else if ('role' in userData) {
    // If role field exists but is null/undefined, remove it entirely for partial updates
    delete formatted.role;
  }
  
  // Only add customerId for new users, not for edits (partial updates)
  if (!isEdit && customerId) {
    formatted.customerId = customerId;
  }

  return formatted;
};

// Export as default object for easier importing
const UserService = {
  getAll,
  create,
  update,
  toggleStatus,
  validateData,
  formatForSubmission
};

export default UserService;
